import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { theme } from '@saesa/ui-kit-front';
import { iconoEmergenciaClimatica, ARROW_UP } from 'assets/images';
import { CADENA_VACIA } from 'utils/constants';
import {
  ATRIBUTO_EXPANDIDO,
  CLAVE_CONTRAER_DISCLAIMER_PEC,
  CLAVE_DESCRIPCION_DISCLAIMER_PEC,
  CLAVE_EXPANDIR_DISCLAIMER_PEC,
  CLAVE_TITULO_DISCLAIMER_PEC,
  EVENTO_CAMBIO_MEDIA,
  PREFIJO_REGLA_MEDIA,
  SEPARACION_DISCLAIMER_BUSCADOR,
  SUFIJO_PX,
  TEST_ID_DISCLAIMER_PEC,
  TEST_ID_TOGGLE_DISCLAIMER_PEC,
  TIPO_FUNCION,
  VALOR_COLAPSADO,
  VALOR_EXPANDIDO,
  VARIABLE_ALTO_DISCLAIMER_PEC,
  VARIANTE_DISCLAIMER_DETALLE,
  VARIANTE_DISCLAIMER_HOME,
} from './disclaimerPec.constants';
import useStyles from './DisclaimerPecStyles';

type VarianteDisclaimerPec = typeof VARIANTE_DISCLAIMER_HOME | typeof VARIANTE_DISCLAIMER_DETALLE;

/**
 * Misma consulta que `theme.breakpoints.down('sm')` en los estilos del mapa.
 * En esta versión de Material-UI ese corte llega hasta antes de `md`.
 */
const consultaAnchoMobile = () => theme.breakpoints.down('sm').replace(PREFIJO_REGLA_MEDIA, '');

/**
 * Indica si la ventana actual corresponde al ancho mobile.
 */
const esViewportMobile = () => typeof window.matchMedia === TIPO_FUNCION && window.matchMedia(consultaAnchoMobile()).matches;

/**
 * Aviso de emergencia climática del mapa de cortes.
 * En mobile parte contraído (solo título) para no tapar el buscador.
 * @param variante home usa tipografía de la tarjeta de búsqueda; detalle, la del dashboard
 * @param className clase extra para ubicarlo en la grilla del dashboard
 */
const DisclaimerPec = ({ variante = VARIANTE_DISCLAIMER_DETALLE, className }: { variante?: VarianteDisclaimerPec; className?: string }) => {
  /**
   * Variable que maneja el uso de estilos.
   */
  const styles = useStyles();

  /**
   * Uso del traductor.
   */
  const { t } = useTranslation();

  /**
   * Referencia al contenedor del aviso para medir su alto.
   */
  const raizRef = useRef<HTMLDivElement>(null);

  /**
   * Indica si el aviso se muestra bajo la tarjeta de búsqueda.
   */
  const esHome = variante === VARIANTE_DISCLAIMER_HOME;

  /**
   * Indica si el ancho de la ventana es mobile.
   */
  const [esMobile, setEsMobile] = useState(esViewportMobile);

  /**
   * Indica si el texto del aviso está visible. En el home mobile parte cerrado.
   */
  const [abierto, setAbierto] = useState(variante !== VARIANTE_DISCLAIMER_HOME);

  /**
   * El aviso muestra solo el título cuando es mobile y está cerrado.
   */
  const colapsado = esMobile && !abierto;

  /**
   * Actualiza el estado mobile cuando cambia el ancho de la ventana.
   */
  useEffect(() => {
    if (typeof window.matchMedia !== TIPO_FUNCION) {
      return undefined;
    }
    /**
     * Consulta de media del breakpoint mobile.
     */
    const media = window.matchMedia(consultaAnchoMobile());
    /**
     * Guarda si la consulta de ancho sigue coincidiendo.
     */
    const actualizar = () => setEsMobile(media.matches);
    actualizar();
    media.addEventListener(EVENTO_CAMBIO_MEDIA, actualizar);
    return () => media.removeEventListener(EVENTO_CAMBIO_MEDIA, actualizar);
  }, []);

  /**
   * Publica el alto del aviso de home en mobile para subir el buscador y el mensaje flotante.
   */
  useEffect(() => {
    if (!esMobile || !esHome || !raizRef.current) {
      document.documentElement.style.removeProperty(VARIABLE_ALTO_DISCLAIMER_PEC);
      return undefined;
    }
    /**
     * Contenedor medido del aviso.
     */
    const nodo = raizRef.current;
    /**
     * Escribe el alto medido más la separación con el buscador.
     */
    const publicarAlto = () => {
      document.documentElement.style.setProperty(
        VARIABLE_ALTO_DISCLAIMER_PEC,
        `${nodo.offsetHeight + SEPARACION_DISCLAIMER_BUSCADOR}${SUFIJO_PX}`
      );
    };
    publicarAlto();
    if (typeof ResizeObserver !== TIPO_FUNCION) {
      return () => {
        document.documentElement.style.removeProperty(VARIABLE_ALTO_DISCLAIMER_PEC);
      };
    }
    /**
     * Vuelve a publicar el alto cuando el aviso cambia de tamaño.
     */
    const observador = new ResizeObserver(publicarAlto);
    observador.observe(nodo);
    return () => {
      observador.disconnect();
      document.documentElement.style.removeProperty(VARIABLE_ALTO_DISCLAIMER_PEC);
    };
  }, [esMobile, esHome, abierto]);

  return (
    <div
      ref={raizRef}
      className={clsx(styles.raiz, { [styles.raizColapsada]: colapsado }, className)}
      data-testid={TEST_ID_DISCLAIMER_PEC}
      data-variante={variante}
      {...{ [ATRIBUTO_EXPANDIDO]: colapsado ? VALOR_COLAPSADO : VALOR_EXPANDIDO }}
    >
      <img src={iconoEmergenciaClimatica} alt={CADENA_VACIA} className={styles.icono} aria-hidden />
      <div className={styles.textos}>
        <div className={styles.filaTitulo}>
          <p className={esHome ? styles.tituloHome : styles.tituloDetalle}>{t(CLAVE_TITULO_DISCLAIMER_PEC)}</p>
          <button
            type="button"
            className={styles.botonChevron}
            aria-expanded={!colapsado}
            aria-label={t(abierto ? CLAVE_CONTRAER_DISCLAIMER_PEC : CLAVE_EXPANDIR_DISCLAIMER_PEC)}
            data-testid={TEST_ID_TOGGLE_DISCLAIMER_PEC}
            onClick={() => setAbierto(valor => !valor)}
          >
            <img src={ARROW_UP} alt={CADENA_VACIA} className={clsx(styles.chevron, { [styles.chevronAbierto]: !abierto })} />
          </button>
        </div>
        {!colapsado && <p className={esHome ? styles.textoHome : styles.textoDetalle}>{t(CLAVE_DESCRIPCION_DISCLAIMER_PEC)}</p>}
      </div>
    </div>
  );
};

DisclaimerPec.defaultProps = {
  variante: VARIANTE_DISCLAIMER_DETALLE,
  className: undefined,
};

export default DisclaimerPec;
